/**
 * Is the pose server reachable right now? One `GET /status`, re-askable.
 *
 * The router gates every trigger except models-failed on this answer
 * (`poseSourceRouter`, step 4): for a user whose own detector works, an
 * unreachable server is strictly worse than a slow local one. It used to be
 * asked exactly once per page load, so a server that went away after boot kept
 * being chosen — every retry sent that user straight back to it until they
 * reloaded. `ServerPoseService` now re-asks at every ALIGNMENT entry.
 *
 * Three properties make that safe to do on every attempt:
 *
 *  - **The previous verdict stands until the new one lands.** The router reads
 *    this on every frame; a transient "pending" would move a healthy session
 *    to the device and spend one of its two allowed switches doing it.
 *  - **Overlapping refreshes share one request.**
 *  - **A hung request cannot hang the verdict.** The timeout is a timer racing
 *    the request, not a hope that `AbortController` makes the request reject —
 *    this codebase targets es5 for old Android WebViews.
 *
 * `/status` answers 503 until the model has loaded, so "failed" covers a
 * server that is reachable but cannot serve yet as well as one that is gone.
 */

import type { ProbeState } from "./poseSourceRouter";

/**
 * How long a reachability check may take before it counts as failed.
 *
 * This asks "is the server there", not "is it fast", and the first request of
 * a page load pays for a whole new connection — DNS, TCP and TLS — on top of the
 * trip itself. On prod, 2026-09-16, an iPhone in Cairo reached us-central1
 * **1.58 s** after sending and the server answered in 5 ms, but the old 1.5 s
 * limit had already failed it; the page-load probe never arrived at all. Every
 * rescue rule behind the probe gate stayed off for that user, with nothing but
 * a warning to show for it.
 *
 * Speed is judged elsewhere, from real frame round trips against
 * `serverPoseMaxRttMs`. And a longer limit costs no user anything: at boot the
 * verdict is "pending" (so: the phone) until the check lands, and at every later
 * refresh the previous verdict stands until the new one does.
 */
export const PROBE_TIMEOUT_MS = 5000;

export interface EndpointProbeDeps {
  url: string;
  fetchImpl: typeof fetch;
  timeoutMs: number;
  /** Called once per failed check. Every PWA log is a Firestore write. */
  onFailure?: () => void;
}

export class EndpointProbe {
  private deps: EndpointProbeDeps;
  private state: ProbeState = "pending";
  private running: Promise<void> | null = null;

  constructor(deps: EndpointProbeDeps) {
    this.deps = deps;
  }

  getState(): ProbeState {
    return this.state;
  }

  /** Re-check reachability. Never rejects. */
  refresh(): Promise<void> {
    if (this.running) return this.running;
    const done = () => {
      this.running = null;
    };
    this.running = this.check().then(done, done);
    return this.running;
  }

  private async check(): Promise<void> {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    let onTimeout: () => void = function () {};
    const timedOut = new Promise<never>(function (_resolve, reject) {
      onTimeout = function () {
        reject(new Error("probe timed out"));
      };
    });
    const timer = setTimeout(function () {
      if (controller) controller.abort();
      onTimeout();
    }, this.deps.timeoutMs);

    let healthy = false;
    try {
      const res = await Promise.race([
        this.deps.fetchImpl(this.deps.url, {
          cache: "no-store",
          signal: controller ? controller.signal : undefined,
        } as RequestInit),
        timedOut,
      ]);
      healthy = !!res && res.ok;
    } catch (e) {
      healthy = false;
    } finally {
      clearTimeout(timer);
    }

    this.state = healthy ? "ok" : "failed";
    if (!healthy && this.deps.onFailure) this.deps.onFailure();
  }
}
